import { Plus, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiRequest } from "../../lib/api";
import { useAdminRegions, useInvalidateAll } from "../../lib/queries";
import type { Destination, DestinationCategory, Region } from "../../types";
import { AdminPhotoField, Feedback, Field, useAction } from "./shared";

const CATEGORIES: DestinationCategory[] = ["playa", "ciudad", "aventura", "cultura"];
const slugify = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export function AdminCatalogPage() {
  usePageHeader("Destinos", "Regiones y destinos que aparecen en Explora.");
  const regions = useAdminRegions();
  const [creating, setCreating] = useState(false);
  if (regions.isLoading) return <div className="page-state"><Spinner label="Cargando catálogo" /></div>;
  return (
    <div className="page-container">
      <div className="section-heading">
        <div><span className="page-eyebrow">Catálogo</span><h2>{regions.data?.length ?? 0} regiones</h2></div>
        <button type="button" className="primary-button solid" onClick={() => setCreating((open) => !open)}><Plus size={16} aria-hidden="true" /> Nueva región</button>
      </div>
      {creating && <RegionForm onDone={() => setCreating(false)} />}
      <div className="admin-stack">
        {(regions.data ?? []).map((region) => <RegionEditor key={region.id} region={region} />)}
      </div>
    </div>
  );
}

function RegionForm({ region, onDone }: { region?: Region; onDone(): void }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [name, setName] = useState(region?.name ?? "");
  const [slug, setSlug] = useState(region?.slug ?? "");
  const [subtitle, setSubtitle] = useState(region?.subtitle ?? "");
  const [featured, setFeatured] = useState(region?.featured ?? "");
  const [flag, setFlag] = useState(region?.flag ?? "");
  const [hint, setHint] = useState(region?.hint ?? "");
  const [sortOrder, setSortOrder] = useState(String(region?.sortOrder ?? 0));
  const [active, setActive] = useState(region?.active ?? true);
  const [photoId, setPhotoId] = useState<string | null>(region?.photoId ?? null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const ok = await action.run("region", async () => {
      const body = { name, slug: slug || slugify(name), subtitle: subtitle || null, featured: featured || null, flag: flag || null, hint: hint || null, sortOrder: Number(sortOrder) || 0, active, photoId };
      await apiRequest(region ? `/api/v1/admin/regions/${region.id}` : "/api/v1/admin/regions", { method: region ? "PATCH" : "POST", body: JSON.stringify(body) });
      await invalidate();
      return region ? "Región actualizada." : "Región creada.";
    });
    if (ok && !region) onDone();
  };
  return (
    <form className="card admin-form" onSubmit={submit}>
      <div className="field-grid">
        <Field label="Nombre" htmlFor={`r-name-${region?.id ?? "new"}`}><input id={`r-name-${region?.id ?? "new"}`} className="standalone-input" value={name} onChange={(event) => { setName(event.target.value); if (!region) setSlug(slugify(event.target.value)); }} required minLength={2} /></Field>
        <Field label="Identificador (URL)" htmlFor={`r-slug-${region?.id ?? "new"}`} hint="Minúsculas, números y guiones."><input id={`r-slug-${region?.id ?? "new"}`} className="standalone-input" value={slug} onChange={(event) => setSlug(event.target.value)} pattern="[a-z0-9]+(-[a-z0-9]+)*" required /></Field>
      </div>
      <div className="field-grid">
        <Field label="Subtítulo" htmlFor={`r-sub-${region?.id ?? "new"}`}><input id={`r-sub-${region?.id ?? "new"}`} className="standalone-input" value={subtitle} onChange={(event) => setSubtitle(event.target.value)} placeholder="París · Roma · Lisboa" maxLength={160} /></Field>
        <Field label="Destacado" htmlFor={`r-feat-${region?.id ?? "new"}`}><input id={`r-feat-${region?.id ?? "new"}`} className="standalone-input" value={featured} onChange={(event) => setFeatured(event.target.value)} placeholder="París" maxLength={80} /></Field>
      </div>
      <div className="field-grid three">
        <Field label="Bandera (emoji)" htmlFor={`r-flag-${region?.id ?? "new"}`}><input id={`r-flag-${region?.id ?? "new"}`} className="standalone-input" value={flag} onChange={(event) => setFlag(event.target.value)} maxLength={8} placeholder="🇫🇷" /></Field>
        <Field label="Pista de la tarjeta" htmlFor={`r-hint-${region?.id ?? "new"}`}><input id={`r-hint-${region?.id ?? "new"}`} className="standalone-input" value={hint} onChange={(event) => setHint(event.target.value)} placeholder="Capital · 5 noches" maxLength={80} /></Field>
        <Field label="Orden" htmlFor={`r-order-${region?.id ?? "new"}`}><input id={`r-order-${region?.id ?? "new"}`} className="standalone-input" inputMode="numeric" value={sortOrder} onChange={(event) => setSortOrder(event.target.value.replace(/\D/g, ""))} /></Field>
      </div>
      <AdminPhotoField kind="region" value={photoId} onChange={setPhotoId} label="Foto de la región" />
      <label className="check-row inline"><input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} /><span>Visible para los clientes</span></label>
      <Feedback error={action.error} notice={action.notice} />
      <div className="inline-actions">
        <button className="primary-button solid" type="submit" disabled={action.busy !== null}>{action.busy === "region" ? <Spinner /> : null} {region ? "Guardar región" : "Crear región"}</button>
        {!region && <button type="button" className="secondary-button" onClick={onDone}>Cancelar</button>}
      </div>
    </form>
  );
}

function RegionEditor({ region }: { region: Region }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const remove = async () => {
    if (!window.confirm(`¿Eliminar la región “${region.name}” y sus ${region.destinationCount} destinos?`)) return;
    await action.run("delete", async () => { await apiRequest(`/api/v1/admin/regions/${region.id}`, { method: "DELETE" }); await invalidate(); });
  };
  return (
    <section className="card region-editor">
      <div className="region-editor-head">
        <div className="region-editor-thumb" style={region.photoUrl ? { backgroundImage: `url(${region.photoUrl})` } : undefined} />
        <div className="region-editor-copy">
          <strong>{region.flag} {region.name} {!region.active && <span className="tag suspended">oculta</span>}</strong>
          <span>/{region.slug} · {region.destinationCount} destinos · {region.subtitle ?? "sin subtítulo"}</span>
        </div>
        <div className="inline-actions">
          <button type="button" className="secondary-button small" onClick={() => setOpen((value) => !value)}>{open ? "Cerrar" : "Editar"}</button>
          <button type="button" className="icon-button danger" onClick={() => void remove()} aria-label="Eliminar región" disabled={action.busy !== null}><Trash2 size={16} aria-hidden="true" /></button>
        </div>
      </div>
      <Feedback error={action.error} notice={null} />
      {open && <RegionForm region={region} onDone={() => setOpen(false)} />}
      <div className="section-heading small">
        <span className="rail-title">Destinos</span>
        <button type="button" className="link-button" onClick={() => setAdding((value) => !value)}><Plus size={14} aria-hidden="true" /> Agregar destino</button>
      </div>
      {adding && <DestinationForm regionId={region.id} onDone={() => setAdding(false)} />}
      <div className="admin-stack tight">
        {region.destinations.map((destination) => <DestinationRow key={destination.id} destination={destination} />)}
        {region.destinations.length === 0 && <p className="rail-empty">Sin destinos en esta región.</p>}
      </div>
    </section>
  );
}

function DestinationRow({ destination }: { destination: Destination }) {
  const [open, setOpen] = useState(false);
  const invalidate = useInvalidateAll();
  const action = useAction();
  const remove = async () => {
    if (!window.confirm(`¿Eliminar el destino “${destination.name}”?`)) return;
    await action.run("delete", async () => { await apiRequest(`/api/v1/admin/destinations/${destination.id}`, { method: "DELETE" }); await invalidate(); });
  };
  return (
    <div className="dest-editor">
      <div className="region-editor-head">
        <div className="region-editor-thumb small" style={destination.photoUrl ? { backgroundImage: `url(${destination.photoUrl})` } : undefined} />
        <div className="region-editor-copy">
          <strong>{destination.name} <span className="tag">{destination.category}</span> {!destination.active && <span className="tag suspended">oculto</span>}</strong>
          <span>{destination.country} · {destination.subtitle ?? "—"} · {destination.price !== null ? `$${destination.price.toLocaleString("es-MX")} ${destination.currency} p/p` : "sin precio"}</span>
        </div>
        <div className="inline-actions">
          <button type="button" className="secondary-button small" onClick={() => setOpen((value) => !value)}>{open ? "Cerrar" : "Editar"}</button>
          <button type="button" className="icon-button danger" onClick={() => void remove()} aria-label="Eliminar destino" disabled={action.busy !== null}><Trash2 size={16} aria-hidden="true" /></button>
        </div>
      </div>
      <Feedback error={action.error} notice={null} />
      {open && <DestinationForm regionId={destination.regionId} destination={destination} onDone={() => setOpen(false)} />}
    </div>
  );
}

function DestinationForm({ regionId, destination, onDone }: { regionId: string; destination?: Destination; onDone(): void }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [name, setName] = useState(destination?.name ?? "");
  const [slug, setSlug] = useState(destination?.slug ?? "");
  const [country, setCountry] = useState(destination?.country ?? "");
  const [subtitle, setSubtitle] = useState(destination?.subtitle ?? "");
  const [category, setCategory] = useState<DestinationCategory>(destination?.category ?? "playa");
  const [price, setPrice] = useState(destination?.price?.toString() ?? "");
  const [currency, setCurrency] = useState<Destination["currency"]>(destination?.currency ?? "MXN");
  const [description, setDescription] = useState(destination?.description ?? "");
  const [season, setSeason] = useState(destination?.season ?? "");
  const [nights, setNights] = useState(destination?.nights?.toString() ?? "");
  const [perks, setPerks] = useState((destination?.perks ?? []).join("\n"));
  const [sortOrder, setSortOrder] = useState(String(destination?.sortOrder ?? 0));
  const [active, setActive] = useState(destination?.active ?? true);
  const [photoId, setPhotoId] = useState<string | null>(destination?.photoId ?? null);
  useEffect(() => { if (!destination) setSlug(slugify(name)); }, [name, destination]);
  const suffix = destination?.id ?? `new-${regionId}`;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const ok = await action.run("dest", async () => {
      const body = {
        regionId, name, slug: slug || slugify(name), country, subtitle: subtitle || null, category,
        price: price === "" ? null : Number(price), currency, description: description.trim() || null, season: season || null, nights: nights === "" ? null : Number(nights),
        perks: perks.split("\n").map((line) => line.trim()).filter(Boolean).slice(0, 8), sortOrder: Number(sortOrder) || 0, active, photoId
      };
      await apiRequest(destination ? `/api/v1/admin/destinations/${destination.id}` : "/api/v1/admin/destinations", { method: destination ? "PATCH" : "POST", body: JSON.stringify(body) });
      await invalidate();
      return destination ? "Destino actualizado." : "Destino creado.";
    });
    if (ok) onDone();
  };
  return (
    <form className="admin-form nested" onSubmit={submit}>
      <div className="field-grid three">
        <Field label="Nombre" htmlFor={`d-name-${suffix}`}><input id={`d-name-${suffix}`} className="standalone-input" value={name} onChange={(event) => setName(event.target.value)} required minLength={2} /></Field>
        <Field label="País" htmlFor={`d-country-${suffix}`}><input id={`d-country-${suffix}`} className="standalone-input" value={country} onChange={(event) => setCountry(event.target.value)} required minLength={2} /></Field>
        <Field label="Identificador (URL)" htmlFor={`d-slug-${suffix}`}><input id={`d-slug-${suffix}`} className="standalone-input" value={slug} onChange={(event) => setSlug(event.target.value)} pattern="[a-z0-9]+(-[a-z0-9]+)*" required /></Field>
      </div>
      <div className="field-grid three">
        <Field label="Subtítulo" htmlFor={`d-sub-${suffix}`}><input id={`d-sub-${suffix}`} className="standalone-input" value={subtitle} onChange={(event) => setSubtitle(event.target.value)} placeholder="Atolones · 7 noches" /></Field>
        <Field label="Categoría" htmlFor={`d-cat-${suffix}`}>
          <select id={`d-cat-${suffix}`} className="standalone-input" value={category} onChange={(event) => setCategory(event.target.value as DestinationCategory)}>
            {CATEGORIES.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </Field>
        <Field label="Precio desde (por persona)" htmlFor={`d-price-${suffix}`}>
          <div className="price-with-currency">
            <input id={`d-price-${suffix}`} className="standalone-input" inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value.replace(/[^\d.]/g, ""))} />
            <select className="standalone-input" value={currency} onChange={(event) => setCurrency(event.target.value as Destination["currency"])} aria-label="Moneda">
              <option value="MXN">MXN</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </Field>
      </div>
      <Field label="Descripción (se muestra al abrir “Más detalles” en la tarjeta)" htmlFor={`d-desc-${suffix}`}><textarea id={`d-desc-${suffix}`} className="standalone-input textarea" rows={3} value={description} onChange={(event) => setDescription(event.target.value)} maxLength={600} placeholder="Qué incluye el viaje, itinerario, hotel…" /></Field>
      <div className="field-grid three">
        <Field label="Temporada" htmlFor={`d-season-${suffix}`}><input id={`d-season-${suffix}`} className="standalone-input" value={season} onChange={(event) => setSeason(event.target.value)} placeholder="Mar – Abr" maxLength={40} /></Field>
        <Field label="Noches" htmlFor={`d-nights-${suffix}`}><input id={`d-nights-${suffix}`} className="standalone-input" inputMode="numeric" value={nights} onChange={(event) => setNights(event.target.value.replace(/\D/g, ""))} /></Field>
        <Field label="Orden" htmlFor={`d-order-${suffix}`}><input id={`d-order-${suffix}`} className="standalone-input" inputMode="numeric" value={sortOrder} onChange={(event) => setSortOrder(event.target.value.replace(/\D/g, ""))} /></Field>
      </div>
      <Field label="Incluye (una línea por beneficio)" htmlFor={`d-perks-${suffix}`}><textarea id={`d-perks-${suffix}`} className="standalone-input textarea" rows={3} value={perks} onChange={(event) => setPerks(event.target.value)} placeholder={"Bungalow sobre el mar\nSnorkel guiado"} /></Field>
      <AdminPhotoField kind="destination" value={photoId} onChange={setPhotoId} label="Foto del destino" />
      <label className="check-row inline"><input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} /><span>Visible para los clientes</span></label>
      <Feedback error={action.error} notice={action.notice} />
      <div className="inline-actions">
        <button className="primary-button solid" type="submit" disabled={action.busy !== null}>{action.busy === "dest" ? <Spinner /> : null} {destination ? "Guardar destino" : "Crear destino"}</button>
        <button type="button" className="secondary-button" onClick={onDone}>Cancelar</button>
      </div>
    </form>
  );
}
